import React from 'react'
import styled from '@emotion/styled'
import {
  AutoComplete,
  Button,
  Popconfirm,
  Select,
  Space,
} from 'antd'
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import {
  PortalArticleCardItem,
  PortalArticleSectionContentItem,
} from '../../../../models/PortalArtigoModel'
import {
  PORTAL_CARD_KIND_OPTIONS,
  PORTAL_LEAD_MAGNET_OPTIONS,
} from '../../helpers/portal-editor-options'
import {
  usePortalPostOptions,
  useSiteCourseOptions,
} from '../../helpers/use-portal-editor-reference-options'
import { PortalEditorField } from '../PortalEditorField'

type CardsBlock = Extract<PortalArticleSectionContentItem, { type: 'cards' }>

interface PortalCardsBlockEditorProps {
  value: CardsBlock
  onChange: (value: CardsBlock) => void
}

export function PortalCardsBlockEditor({ value, onChange }: PortalCardsBlockEditorProps) {
  const block = value || { type: 'cards', items: [] }
  const items = block.items || []
  const selectedCourseIds = items
    .filter((item): item is Extract<PortalArticleCardItem, { kind: 'course' }> => (
      item.kind === 'course'
    ))
    .map((item) => item.courseId)
    .filter(Boolean)
  const { courseOptions, loadingCursos } = useSiteCourseOptions(selectedCourseIds)
  const { portalPostOptions, loadingArtigos } = usePortalPostOptions()

  const updateItem = (index: number, item: PortalArticleCardItem) => {
    onChange({
      ...block,
      items: items.map((currentItem, currentIndex) => (
        currentIndex === index ? item : currentItem
      )),
    })
  }

  const addItem = () => {
    onChange({
      ...block,
      items: [...items, { kind: 'portalPost', slug: '' }],
    })
  }

  const removeItem = (index: number) => {
    onChange({
      ...block,
      items: items.filter((_, currentIndex) => currentIndex !== index),
    })
  }

  const moveItem = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction
    if (targetIndex < 0 || targetIndex >= items.length) return

    const nextItems = [...items]
    const [item] = nextItems.splice(index, 1)
    nextItems.splice(targetIndex, 0, item)
    onChange({ ...block, items: nextItems })
  }

  const changeKind = (index: number, kind: PortalArticleCardItem['kind']) => {
    if (kind === 'course') updateItem(index, { kind, courseId: 0 })
    if (kind === 'leadMagnet') updateItem(index, { kind, slug: '' })
    if (kind === 'portalPost') updateItem(index, { kind, slug: '' })
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      {items.map((item, index) => (
        <CardItem key={index}>
          <CardItemHeader>
            <strong>Card {index + 1}</strong>
            <Space>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => moveItem(index, -1)}
              />
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === items.length - 1}
                onClick={() => moveItem(index, 1)}
              />
              <Popconfirm
                title="Remover card"
                description="Tem certeza que deseja remover este card?"
                okText="Remover"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                onConfirm={() => removeItem(index)}
              >
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          </CardItemHeader>

          <PortalEditorField label="Tipo do card">
            <Select
              value={item.kind}
              options={PORTAL_CARD_KIND_OPTIONS}
              onChange={(kind) => changeKind(index, kind)}
            />
          </PortalEditorField>

          {item.kind === 'course' && (
            <PortalEditorField label="Curso">
              <Select
                showSearch
                loading={loadingCursos}
                optionFilterProp="label"
                value={item.courseId}
                options={courseOptions}
                placeholder="Selecione o curso"
                style={{ width: '100%' }}
                onChange={(courseId) => updateItem(index, { ...item, courseId })}
              />
            </PortalEditorField>
          )}

          {item.kind === 'leadMagnet' && (
            <PortalEditorField label="Lead magnet">
              <Select
                showSearch
                optionFilterProp="label"
                value={item.slug}
                options={PORTAL_LEAD_MAGNET_OPTIONS}
                placeholder="Selecione o lead magnet"
                style={{ width: '100%' }}
                onChange={(slug) => updateItem(index, { ...item, slug })}
              />
            </PortalEditorField>
          )}

          {item.kind === 'portalPost' && (
            <PortalEditorField label="Slug do artigo">
              <AutoComplete
                value={item.slug}
                options={portalPostOptions}
                placeholder="slug-do-artigo"
                style={{ width: '100%' }}
                disabled={loadingArtigos}
                filterOption={(inputValue, option) =>
                  String(option?.label || '').toLowerCase().includes(inputValue.toLowerCase()) ||
                  String(option?.value || '').toLowerCase().includes(inputValue.toLowerCase())
                }
                onChange={(slug) => updateItem(index, { ...item, slug })}
              />
            </PortalEditorField>
          )}
        </CardItem>
      ))}

      <Button icon={<PlusOutlined />} onClick={addItem}>
        Adicionar card
      </Button>
    </Space>
  )
}

const CardItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 12px;
  border: 1px solid #e1e4ed;
  border-radius: 6px;
  background: #f9fafb;
`

const CardItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`
